<div class="flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 space-y-2 border-b border-zinc-200 p-4 dark:border-zinc-800">
    <h4 class="text-sm font-semibold text-primary">Saved reports</h4>
    <p class="text-xs text-secondary">
      Snapshots of creators joined, briefs posted, creators hired, and brands joined for a date
      range. Requires GodMode, Account Manager, or Creator Admin.
    </p>
    <p class="m-0 text-sm font-medium tabular-nums text-secondary" aria-live="polite">
      {{ recordCountLabel }}
    </p>
  </div>

  @if (loadingState === 'error' && error) {
    <div class="mx-4 mt-4 shrink-0 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900/50 dark:bg-red-950/40">
      <p class="m-0 text-sm text-red-700 dark:text-red-300">{{ error }}</p>
    </div>
  }

  <div class="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-zinc-950">
    <div class="min-h-0 flex-1 overflow-auto scrollbar-transparent">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 text-sm dark:divide-zinc-700">
          <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-zinc-800/80">
            <tr>
              <th scope="col" class="px-4 py-3 text-left font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex items-center gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('startDate')"
                  [class.dark:text-indigo-400]="isSortedBy('startDate')"
                  (click)="toggleSort('startDate')"
                  [disabled]="isLoading"
                  aria-label="Sort by period"
                >
                  <span>Period</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('days')"
                  [class.dark:text-indigo-400]="isSortedBy('days')"
                  (click)="toggleSort('days')"
                  [disabled]="isLoading"
                  aria-label="Sort by days"
                >
                  <span>Days</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('creatorsJoined')"
                  [class.dark:text-indigo-400]="isSortedBy('creatorsJoined')"
                  (click)="toggleSort('creatorsJoined')"
                  [disabled]="isLoading"
                  aria-label="Sort by creators joined"
                >
                  <span>Creators joined</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('briefsPosted')"
                  [class.dark:text-indigo-400]="isSortedBy('briefsPosted')"
                  (click)="toggleSort('briefsPosted')"
                  [disabled]="isLoading"
                  aria-label="Sort by briefs posted"
                >
                  <span>Briefs posted</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('creatorsHired')"
                  [class.dark:text-indigo-400]="isSortedBy('creatorsHired')"
                  (click)="toggleSort('creatorsHired')"
                  [disabled]="isLoading"
                  aria-label="Sort by creators hired"
                >
                  <span>Creators hired</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                <button
                  type="button"
                  class="inline-flex w-full items-center justify-end gap-1 rounded hover:text-indigo-700 dark:hover:text-indigo-400"
                  [class.text-indigo-600]="isSortedBy('brandsJoined')"
                  [class.dark:text-indigo-400]="isSortedBy('brandsJoined')"
                  (click)="toggleSort('brandsJoined')"
                  [disabled]="isLoading"
                  aria-label="Sort by brands joined"
                >
                  <span>Brands joined</span>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 text-right font-semibold text-gray-700 dark:text-zinc-300">
                Saved
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-950">
            @if (isLoading && !hasRowData) {
              <tr>
                <td colspan="7" class="px-4 py-8">
                  <atom-loading-inline title="Loading reports" message="Fetching saved reports…" />
                </td>
              </tr>
            } @else if (hasRowData) {
              @for (row of rows; track row.id) {
                <tr class="hover:bg-gray-50 dark:hover:bg-zinc-900/50">
                  <td class="px-4 py-3 font-medium text-gray-900 dark:text-zinc-100">
                    {{ periodLabel(row) }}
                  </td>
                  <td class="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                    {{ row.days }}
                  </td>
                  <td class="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                    {{ row.creatorsJoined.toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                    {{ row.briefsPosted.toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                    {{ row.creatorsHired.toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-right tabular-nums text-gray-900 dark:text-zinc-100">
                    {{ row.brandsJoined.toLocaleString() }}
                  </td>
                  <td class="px-4 py-3 text-right text-xs text-secondary">
                    {{ formatDate(row.createdDate) }}
                  </td>
                </tr>
              }
            } @else if (!isLoading) {
              <tr>
                <td colspan="7" class="px-4 py-8 text-center text-sm text-secondary">
                  No saved reports yet. Generate one from the dashboard and save it.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>
